/**
 * SE-6490 — the pipeline card's signals row (Player Card v7, row 3).
 *
 * @remarks
 * The design renders the card's two report-derived signals as PILLS rather
 * than as `Label: value` text: a verdict pill and a paperclip-and-count
 * reports pill. Both come from `StatusChip` / `LabelPill` / `ReportsChip` —
 * molecules SE-5527 promoted out of `MobileTargetCardView`, which is the
 * registry-mapped implementation of this same Figma component, so their tone
 * pairings are the ones SE-5522 pixel-audited against it.
 *
 * **Replaces**, not restyles, the previous `Age: · Contract: · GPR:` /
 * `Price: · Reports: · Verdict:` grid (SE-5492). Age, contract and GPR moved
 * up into the identity/context rows where the design puts them; the price
 * range left the card face entirely by product decision (2026-07-31) and
 * remains in the preview drawer (SE-5494).
 *
 * **On the verdict's wording.** The schema has no scout-authored verdict
 * field, so — exactly as the preview drawer does (SE-5494) — the "verdict" is
 * the most recent report's overall score. The pill therefore reads
 * `Verdict 8.4` rather than the design's sample `Starting XI`: labelling it is
 * what stops a bare number in a green pill from being read as a grade. The
 * check icon marks "this player has been scouted and scored", not "the score
 * is good". A player with no scored report keeps the field present as a muted
 * `Verdict —` rather than dropping it, so the field is never silently missing.
 * @module components/organisms/custom/ui/PlayerCardSignals/PlayerCardSignalsContainer
 */
import React from "react";

import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Stack } from "@/components/atoms/Stack";
import LabelPill from "@/components/molecules/LabelPill";
import ReportsChip from "@/components/molecules/ReportsChip";
import StatusChip from "@/components/molecules/StatusChip";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/** Shown when a player has no report carrying a scored verdict. */
const NO_VERDICT = "—";

/** Props for PlayerCardSignals */
interface PlayerCardSignalsProps {
  /**
   * SE-5492: total scout reports attached to the player (server-side count,
   * not the length of the fetched report list). Defaults to 0.
   */
  readonly reportsCount?: number;
  /**
   * SE-5492: the latest report's overall score. `null`/absent renders the
   * muted em-dash pill — the same "no value" convention the drawer uses.
   */
  readonly verdict?: string | null;
  /**
   * SE-5497: whether the card is over its stage's configured time target.
   * The day COUNT left the card face in SE-6490; the flag it drives did not.
   */
  readonly isStale?: boolean;
}

/**
 * The card's report-derived signals, as pills.
 * @param root0 - Component props
 * @param root0.reportsCount - Total scout reports attached to the player
 * @param root0.verdict - Latest report's overall score, or null
 * @param root0.isStale - Whether the card is over its stage's time target
 */
const PlayerCardSignals = ({
  reportsCount = 0,
  verdict,
  isStale = false,
}: PlayerCardSignalsProps) => (
  <Stack
    direction="horizontal"
    className="flex-wrap items-center gap-1"
    testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_SIGNALS}
  >
    {verdict != null ? (
      <StatusChip
        label={`Verdict ${verdict}`}
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_VERDICT}
      />
    ) : (
      <LabelPill
        label={`Verdict ${NO_VERDICT}`}
        tone="muted"
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_VERDICT}
      />
    )}

    {/* The count is always rendered — 0 is a meaningful answer ("no one has
        scouted this player"), so the field is never silently missing. The
        shared chip hides itself at 0, so the pipeline card renders its own
        zero state rather than letting the field disappear. */}
    {reportsCount > 0 ? (
      <ReportsChip
        avgScoutRating={null}
        scoutReportCount={reportsCount}
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_REPORTS}
      />
    ) : (
      <LabelPill
        label="0 reports"
        tone="subtle"
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_REPORTS}
      />
    )}

    {isStale && (
      <Badge
        action="warning"
        size="sm"
        variant="solid"
        {...{ "data-testid": TEST_ID_PLAYER_KANBAN.PLAYER_CARD_STALE_FLAG }}
      >
        <BadgeText>Stale</BadgeText>
      </Badge>
    )}
  </Stack>
);

export default PlayerCardSignals;
